import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Code2, FileText, Folder, GitBranch, GitCommitHorizontal, Search, X } from "lucide-react";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { wanneer } from "./RepoVastleggingen";
import { diffKleur } from "@/lib/diff-kleur";
import RepoTakken from "./RepoTakken";
import RepoPulls from "./RepoPulls";
import RepoIssues from "./RepoIssues";
import PullSamenvoegen from "./PullSamenvoegen";
import RepoSpiegel from "./RepoSpiegel";
import RepoMeenemen from "./RepoMeenemen";
import RepoNaarGithub from "./RepoNaarGithub";
import RepoTeam from "./RepoTeam";

/**
 * EG2 (opdracht Frank 31 oktober 2026): een repo bekijken, bestanden en geschiedenis, ook op de telefoon.
 *
 * Indeling naar GitHub, zodat Frank zich meteen thuis voelt: bovenaan de takkiezer, dan de tabbladen Code (pad als
 * kruimelspoor, mappen eerst, een bestand met regelnummers) en Vastleggingen (boodschap, schrijver, tijd, korte
 * sleutel; een vastlegging opent met de gewijzigde bestanden en de diff in rood en groen). API-vorm naar Gitea
 * (`routers/api/v1/repo/tree.go`, `file.go`, `commits.go`). Alles via `/api/repos`.
 */
const STATUS = { A: "nieuw", M: "gewijzigd", D: "verwijderd", R: "hernoemd" };

export { diffKleur };

export default function RepoDetail({ repo, onTerug, onOpenInCode = null, onLosIssueOp = null }) {
  const { t, taal } = useTaal();
  const [tak, setTak] = useState(repo.default_branch || "main");
  const [tab, setTab] = useState("code");
  const [pad, setPad] = useState("");
  const [rijen, setRijen] = useState([]);
  const [bestand, setBestand] = useState(null);
  const [commits, setCommits] = useState([]);
  const [gekozen, setGekozen] = useState(null);
  // LC4: zoeken in de code van deze tak (GET /repos/{id}/zoek, symbolen eerst). null = geen zoekvraag actief.
  const [vraag, setVraag] = useState("");
  const [treffers, setTreffers] = useState(null);
  const [zoekFout, setZoekFout] = useState("");
  // EG3: de takken, bijgewerkt na maken of verwijderen.
  const [takkenLijst, setTakkenLijst] = useState(repo.branches || [tak]);
  // GL225 (glasronde 0.9.225): na samenvoegen veranderde main op de server, maar boom, vastleggingen en de teller
  // bleven op de oude stand tot Repo's opnieuw openging. Elke verhoging laadt ze opnieuw, en de takken ook.
  const [versie, setVersie] = useState(0);
  // EG9: een spiegel van GitHub is hier alleen te lezen; na Losmaken is NovaMund de bron en kan hij weer in Code.
  const [bron, setBron] = useState(repo.bron || null);
  const spiegel = bron?.soort === "github";
  function ververs() {
    setVersie((v) => v + 1);
    axios.get(`${API}/repos/${repo.id}`).then((r) => r.data?.branches && setTakkenLijst(r.data.branches)).catch(() => {});
  }

  useEffect(() => {
    setBestand(null);
    axios.get(`${API}/repos/${repo.id}/boom`, { params: { tak, pad } }).then((r) => setRijen(r.data)).catch(() => setRijen([]));
  }, [repo.id, tak, pad, versie]);

  useEffect(() => {
    setGekozen(null);
    axios.get(`${API}/repos/${repo.id}/commits`, { params: { tak } }).then((r) => setCommits(r.data)).catch(() => setCommits([]));
  }, [repo.id, tak, versie]);

  function open(rij) {
    if (rij.soort === "map") return setPad(rij.pad);
    axios.get(`${API}/repos/${repo.id}/bestand`, { params: { tak, pad: rij.pad } }).then((r) => setBestand(r.data)).catch(() => {});
  }

  function zoek(e) {
    e.preventDefault();
    setZoekFout("");
    if (vraag.trim().length < 3) { setTreffers(null); setZoekFout(t("Zoeken vraagt minstens 3 tekens.")); return; }
    axios.get(`${API}/repos/${repo.id}/zoek`, { params: { tak, q: vraag.trim() } })
      .then((r) => setTreffers(r.data.treffers))
      .catch(() => { setTreffers(null); setZoekFout(t("Zoeken lukte niet.")); });
  }

  function wisZoek() { setVraag(""); setTreffers(null); setZoekFout(""); }

  function kies(c) {
    axios.get(`${API}/repos/${repo.id}/vastlegging/${c.sha}`).then((r) => setGekozen(r.data)).catch(() => {});
  }

  const delen = pad ? pad.split("/") : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2" data-testid="repo-detail">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" className="tap-target" onClick={onTerug} aria-label={t("Terug naar de repo's")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-semibold">{repo.name}</span>
        <label className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs">
          <GitBranch className="h-3 w-3" />
          <select value={tak} onChange={(e) => { setTak(e.target.value); setPad(""); setTreffers(null); }} aria-label={t("Tak")}
                  className="bg-transparent" data-testid="tak-kiezer">
            {takkenLijst.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        {/* Code op een teamrepo van een ander lid (3 nov): een kijker leest alleen, dus geen werkruimte. */}
        {onOpenInCode && !spiegel && repo?.rol !== "viewer" && (
          <Button size="sm" className="tap-target ml-auto" onClick={() => onOpenInCode(repo)} data-testid="repo-open-in-code">
            <Code2 className="h-4 w-4 mr-1" />{t("Open in Code")}
          </Button>
        )}
      </div>

      <RepoTeam repo={repo} />

      {spiegel && (
        <RepoSpiegel repo={{ ...repo, bron }} onVeranderd={(nieuw) => { setBron(nieuw?.bron || null); ververs(); }} />
      )}

      <div className="flex gap-1 overflow-x-auto border-b text-sm" role="tablist">
        {[["code", t("Code")], ["vastleggingen", t("Vastleggingen")], ["takken", t("Takken")], ["issues", t("Issues")], ["pulls", t("Pull requests")], ["meenemen", t("Meenemen")]].map(([id, naam]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
                  data-testid={`tab-${id}`}
                  className={`tap-target px-3 py-1.5 ${tab === id ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}>
            {naam}{id === "vastleggingen" ? ` (${commits.length})` : id === "takken" ? ` (${takkenLijst.length})` : ""}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === "code" && (
          <div className="space-y-2">
            <form onSubmit={zoek} className="flex items-center gap-1" role="search" data-testid="repo-zoek">
              <label className="flex min-w-0 flex-1 items-center gap-1 rounded-md border px-2 py-1 text-sm">
                <Search className="h-3 w-3 shrink-0 text-muted-foreground" />
                <input value={vraag} onChange={(e) => setVraag(e.target.value)} placeholder={t("Zoek in de code")}
                       aria-label={t("Zoek in de code")} className="min-w-0 flex-1 bg-transparent outline-none"
                       data-testid="repo-zoekvraag" />
              </label>
              {treffers !== null && (
                <Button type="button" variant="ghost" size="icon" className="tap-target" onClick={wisZoek} aria-label={t("Wis de zoekvraag")}>
                  <X className="h-4 w-4" />
                </Button>
              )}
              <Button type="submit" size="sm" variant="outline" className="tap-target">{t("Zoek")}</Button>
            </form>
            {zoekFout && <p className="text-xs text-muted-foreground" data-testid="repo-zoekfout">{zoekFout}</p>}
            {treffers !== null && (
              <ul className="divide-y rounded-lg border" data-testid="repo-treffers">
                <li className="px-3 py-1.5 text-xs text-muted-foreground">{treffers.length} {t("treffers")}</li>
                {treffers.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">{t("Geen treffers.")}</li>}
                {treffers.map((x, i) => (
                  <li key={`${x.pad}:${x.nr}:${i}`}>
                    <button type="button" onClick={() => { setTreffers(null); open({ soort: "bestand", pad: x.pad }); }}
                            data-testid={`treffer-${x.pad}:${x.nr}`}
                            className="tap-target flex w-full flex-col px-3 py-1.5 text-left hover:bg-muted/50">
                      <span className="break-all text-xs text-muted-foreground">{x.pad}:{x.nr}{x.soort !== "regel" ? ` · ${t(x.soort)}` : ""}</span>
                      <code className="break-all text-xs">{x.tekst}</code>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <nav className="flex flex-wrap items-center gap-1 text-sm" data-testid="kruimelspoor">
              <button type="button" className="text-primary hover:underline" onClick={() => setPad("")}>{repo.name}</button>
              {delen.map((d, i) => (
                <span key={i} className="flex items-center gap-1">
                  <span className="text-muted-foreground">/</span>
                  <button type="button" className="text-primary hover:underline" onClick={() => setPad(delen.slice(0, i + 1).join("/"))}>{d}</button>
                </span>
              ))}
            </nav>
            {bestand ? (
              <div className="rounded-lg border" data-testid="bestand-inhoud">
                <div className="flex items-center gap-2 border-b px-3 py-1.5 text-xs text-muted-foreground">
                  <FileText className="h-3 w-3" />{bestand.pad}
                  <button type="button" className="ml-auto text-primary hover:underline" onClick={() => setBestand(null)}>{t("Terug naar de map")}</button>
                </div>
                {bestand.binair || bestand.te_groot ? (
                  <p className="p-3 text-sm text-muted-foreground" data-testid="bestand-niet-getoond">
                    {bestand.binair ? t("Dit is een binair bestand; het wordt niet getoond.") : t("Dit bestand is te groot om hier te tonen.")}
                  </p>
                ) : (
                  <pre className="overflow-x-auto p-0 text-xs leading-5"><code>{(bestand.tekst || "").split("\n").map((r, i, alle) => (
                    i === alle.length - 1 && r === "" ? null : (
                      <div key={i} className="flex"><span className="w-10 shrink-0 select-none pr-2 text-right text-muted-foreground">{i + 1}</span><span className="whitespace-pre pr-3">{r}</span></div>
                    )))}</code></pre>
                )}
              </div>
            ) : (
              <ul className="divide-y rounded-lg border" data-testid="repo-boom">
                {rijen.map((r) => (
                  <li key={r.pad}>
                    <button type="button" onClick={() => open(r)} data-testid={`boom-${r.pad}`}
                            className="tap-target flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50">
                      {r.soort === "map" ? <Folder className="h-4 w-4 text-sky-600" /> : <FileText className="h-4 w-4 text-muted-foreground" />}
                      <span className="min-w-0 flex-1 truncate">{r.naam}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "takken" && (
          <RepoTakken repo={repo} takken={takkenLijst}
                      onTakken={(lijst) => { setTakkenLijst(lijst); if (!lijst.includes(tak)) setTak(repo.default_branch || "main"); }} />
        )}

        {tab === "issues" && <RepoIssues repo={repo} onLosOp={onLosIssueOp} />}

        {/* CA6: als bundel of zip meenemen, en hoe je van buiten NovaMund kloont. */}
        {tab === "meenemen" && <RepoMeenemen repo={repo} tak={tak} />}
        {/* G6: alleen een repo die van GitHub kwam en waar NovaMund de bron is. */}
        {tab === "meenemen" && bron?.adres && !spiegel && <RepoNaarGithub repo={repo} takken={takkenLijst} />}

        {tab === "pulls" && <RepoPulls repo={repo} takken={takkenLijst} Extra={PullSamenvoegen} onGewijzigd={ververs} />}

        {tab === "vastleggingen" && !gekozen && (
          <ol className="divide-y rounded-lg border" data-testid="repo-commits">
            {commits.map((c) => (
              <li key={c.sha}>
                <button type="button" onClick={() => kies(c)} data-testid={`commit-${c.kort}`}
                        className="tap-target flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted/50">
                  <GitCommitHorizontal className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-sm">{c.boodschap}</span>
                    <span className="block text-xs text-muted-foreground">{c.schrijver} · {wanneer(c.tijd, taal)}</span>
                  </span>
                  <code className="shrink-0 text-xs text-muted-foreground">{c.kort}</code>
                </button>
              </li>
            ))}
          </ol>
        )}

        {tab === "vastleggingen" && gekozen && (
          <div className="space-y-2" data-testid="vastlegging-detail">
            <button type="button" className="text-sm text-primary hover:underline" onClick={() => setGekozen(null)}>{t("Terug naar de vastleggingen")}</button>
            <div className="rounded-lg border p-3">
              <p className="whitespace-pre-wrap break-words text-sm font-semibold">{gekozen.boodschap}</p>
              <p className="text-xs text-muted-foreground">{gekozen.schrijver} · {wanneer(gekozen.tijd, taal)} · <code>{gekozen.kort}</code></p>
            </div>
            <ul className="space-y-1 text-sm" data-testid="vastlegging-bestanden">
              {gekozen.bestanden.map((b) => (
                <li key={b.pad} className="flex gap-2"><span className="w-20 shrink-0 text-xs text-muted-foreground">{t(STATUS[b.status] || b.status)}</span><span className="break-all">{b.pad}</span></li>
              ))}
            </ul>
            <pre className="overflow-x-auto rounded-lg border text-xs leading-5" data-testid="vastlegging-diff"><code>{gekozen.diff.split("\n").map((r, i) => (
              <div key={i} className={`whitespace-pre px-3 ${diffKleur(r)}`}>{r || " "}</div>
            ))}</code></pre>
            {gekozen.afgekapt && <p className="text-xs text-muted-foreground">{t("De diff is ingekort; de eerste 400 regels staan hier.")}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
